import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Leaf, Sun, Droplets, Wind, TreePine, Recycle, ArrowRight, Users, Heart, Bike } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

// ─── ECO HERO ───
export function EcoHero() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'hero');
  const c = {
    subheading: "Live in Harmony with Nature",
    heading: "Where Green Meets Grand.",
    description: "India's most sustainable township — solar-powered homes, organic gardens, and nature trails.",
    buttonText: "Explore Township",
    buttonLink: "/township",
    imageUrl: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=1920&q=80",
    stats: [{ value: "80%", label: "Green Cover" }, { value: "Zero", label: "Carbon Footprint" }, { value: "500+", label: "Trees Planted" }],
    ...content,
  };
  const defaultStatIcons = [TreePine, Wind, Leaf];
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={c.imageUrl} alt="Nature" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/60 to-transparent" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 backdrop-blur-sm rounded-full mb-6">
            <Leaf className="w-4 h-4 text-accent" />
            <span className="text-accent text-xs font-semibold uppercase tracking-wider">{c.subheading}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-semibold text-primary-foreground leading-tight mb-6">{c.heading}</h1>
          <p className="text-primary-foreground/80 text-lg mb-10">{c.description}</p>
          <Link to={c.buttonLink || "/township"} className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-semibold text-sm uppercase rounded-full hover:bg-accent/90 transition-all">
            {c.buttonText} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}
          className="flex flex-wrap gap-4 mt-16">
          {(c.stats || []).map((s: any, i: number) => {
            const Icon = defaultStatIcons[i % defaultStatIcons.length];
            return (
              <div key={s.label} className="bg-card/90 backdrop-blur-sm rounded-2xl p-5 flex items-center gap-4 shadow-lg">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center"><Icon className="w-6 h-6 text-accent" /></div>
                <div><div className="text-2xl font-bold">{s.value}</div><div className="text-muted-foreground text-xs">{s.label}</div></div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

// ─── ECO GREEN FEATURES ───
export function EcoGreenFeatures() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'green-features');
  const c = {
    heading: "Sustainable by Design",
    description: "Every element crafted for minimal environmental impact.",
    items: [
      { title: "Solar Powered", description: "100% solar energy for common areas and street lighting." },
      { title: "Rainwater Harvesting", description: "Advanced water recycling and conservation systems." },
      { title: "Waste Management", description: "Zero-waste community with composting and recycling." },
      { title: "Natural Ventilation", description: "Cross-ventilated designs reducing AC dependency by 40%." },
      { title: "Native Gardens", description: "Indigenous flora supporting local biodiversity." },
      { title: "Organic Farming", description: "Community organic garden for fresh, pesticide-free produce." },
    ],
    ...content,
  };
  const defaultIcons = [Sun, Droplets, Recycle, Wind, TreePine, Leaf];
  return (
    <section className="py-20 bg-secondary">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">{c.heading}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{c.description}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(c.items || []).map((f: any, i: number) => {
            const Icon = defaultIcons[i % defaultIcons.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-card p-8 rounded-2xl hover:shadow-lg transition-shadow group">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 group-hover:bg-accent flex items-center justify-center mb-5 transition-all duration-300">
                  <Icon className="w-7 h-7 text-accent group-hover:text-accent-foreground transition-colors" />
                </div>
                <h3 className="font-serif text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm">{f.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── ECO COMMUNITY LIVING ───
export function EcoCommunityLiving() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'community-living');
  const c = {
    heading: "Thriving Community",
    description: "A township designed for meaningful connections.",
    items: [
      { title: "Community Events", description: "Regular farmer's markets, yoga sessions, and cultural events.", imageUrl: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=500&q=80" },
      { title: "Car-Free Zones", description: "Dedicated cycling and walking paths throughout the township.", imageUrl: "https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=500&q=80" },
      { title: "Wellness Center", description: "Yoga studio, spa, and naturopathy center for holistic living.", imageUrl: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=500&q=80" },
    ],
    ...content,
  };
  const defaultIcons = [Users, Bike, Heart];
  return (
    <section className="py-20 bg-card">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">{c.heading}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(c.items || []).map((item: any, i: number) => {
            const Icon = defaultIcons[i % defaultIcons.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="rounded-2xl overflow-hidden bg-secondary group cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img src={item.imageUrl || '/placeholder.svg'} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5 text-accent" />
                    <h3 className="font-serif text-lg font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── ECO SUSTAINABILITY STATS ───
export function EcoSustainabilityStats() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'sustainability-stats');
  const c = {
    heading: "Our Green Impact",
    description: "Measurable sustainability milestones.",
    stats: [
      { value: "2.5M", label: "Liters Water Saved Annually" },
      { value: "1200", label: "Tons CO₂ Reduced" },
      { value: "95%", label: "Waste Recycled" },
      { value: "10K+", label: "Trees in Township" },
    ],
    ...content,
  };
  const defaultIcons = [Droplets, Wind, Recycle, TreePine];
  return (
    <section className="py-20 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">{c.heading}</h2>
          {c.description && <p className="text-primary-foreground/60">{c.description}</p>}
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {(c.stats || []).map((s: any, i: number) => {
            const Icon = defaultIcons[i % defaultIcons.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="text-center p-8 rounded-2xl border border-primary-foreground/10 backdrop-blur-sm">
                <Icon className="w-8 h-8 text-accent mx-auto mb-4" />
                <div className="text-3xl md:text-4xl font-bold mb-2">{s.value}</div>
                <div className="text-primary-foreground/60 text-sm">{s.label}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
